import { type GetApiJingleCollectionsQueryParams } from '@/shared/api'
import { combine, createStore, sample } from 'effector'
import { createGate } from 'effector-react'

import {
  jingleCollectionCreate,
  jingleCollectionDelete,
  jingleCollectionUpdate,
} from '../api'
import {
  type JingleCollectionsQueryModelWithPagination,
  type JingleCollectionsSortState,
  createJingleCollectionsQueryModel,
} from '../lib/jingle-collections-query-model'

interface JingleCollectionsTableGateProps {
  initialFilters?: GetApiJingleCollectionsQueryParams
  initialPage?: number
  initialPageSize?: number
  initialSearch?: string
  initialSort?: JingleCollectionsSortState
}
export const JingleCollectionsTableGate =
  createGate<JingleCollectionsTableGateProps>()

export const JingleCollectionsTableJingleCollectionsQueryModel =
  createJingleCollectionsQueryModel({
    usePagination: true,
    accumulate: false,
  }) as JingleCollectionsQueryModelWithPagination
const { availableSet, unavailableSet } =
  JingleCollectionsTableJingleCollectionsQueryModel
export const {
  //   Query
  query: jingleCollectionsTableQuery,
  //   Init
  $filters: $jingleCollectionsTableFilters,
  filtersInit: jingleCollectionsTableFiltersInit,
  filtersChanged: jingleCollectionsTableFiltersChanged,
  filtersReset: jingleCollectionsTableFiltersReset,
  //   Search
  $search: $jingleCollectionsTableSearch,
  searchChanged: jingleCollectionsTableSearchChanged,
  searchReset: jingleCollectionsTableSearchReset,
  //   Sort
  $sort: $jingleCollectionsTableSort,
  sortChanged: jingleCollectionsTableSortChanged,
  sortReset: jingleCollectionsTableSortReset,
  //   Pagination
  $page: $jingleCollectionsTablePage,
  pageChanged: jingleCollectionsTablePageChanged,
  pageReset: jingleCollectionsTablePageReset,
  $pageSize: $jingleCollectionsTablePageSize,
  pageSizeChanged: jingleCollectionsTablePageSizeChanged,
  //   Refresh
  refreshed: jingleCollectionsTableRefreshed,
} = JingleCollectionsTableJingleCollectionsQueryModel

sample({
  clock: JingleCollectionsTableGate.open,
  target: availableSet,
})

// Search input

export const $jingleCollectionsTableSearchInputValue = createStore('')
  .on(jingleCollectionsTableSearchChanged, (_, q) => q)
  .reset(jingleCollectionsTableSearchReset)

// Setup initial params
// Page
sample({
  clock: JingleCollectionsTableGate.open,
  source: combine(
    JingleCollectionsTableGate.state,
    ({ initialPage }) => initialPage || null,
  ),
  filter: (page) => page !== null,
  target: jingleCollectionsTablePageChanged,
})
// Page Size
sample({
  clock: JingleCollectionsTableGate.open,
  source: combine(
    JingleCollectionsTableGate.state,
    ({ initialPageSize }) => initialPageSize || null,
  ),
  filter: (pageSize) => pageSize !== null,
  target: jingleCollectionsTablePageSizeChanged,
})
// Search
sample({
  clock: JingleCollectionsTableGate.open,
  source: combine(
    JingleCollectionsTableGate.state,
    ({ initialSearch }) => initialSearch || null,
  ),
  filter: (search) => search !== null,
  target: jingleCollectionsTableSearchChanged,
})
// Sort
sample({
  clock: JingleCollectionsTableGate.open,
  source: combine(
    JingleCollectionsTableGate.state,
    ({ initialSort }) => initialSort || null,
  ),
  filter: (sort) => sort !== null,
  target: jingleCollectionsTableSortChanged,
})
// Filters
sample({
  source: JingleCollectionsTableGate.state,
  clock: availableSet,
  fn: ({ initialFilters: params }) => params || {},
  target: jingleCollectionsTableFiltersInit,
})

sample({
  clock: JingleCollectionsTableGate.close,
  target: unavailableSet,
})

// Decrease page if there is only one record on page and it's being deleted
sample({
  source: combine({
    page: $jingleCollectionsTablePage,
    queryData: jingleCollectionsTableQuery.$data,
  }),
  clock: jingleCollectionDelete.finished.success,
  filter: ({ queryData, page }) =>
    !queryData?.items.length || (queryData?.items.length <= 1 && page > 1),
  fn: ({ page }) => page - 1,
  target: jingleCollectionsTablePageChanged,
})

sample({
  clock: [
    jingleCollectionCreate.finished.success,
    jingleCollectionUpdate.finished.success,
    jingleCollectionDelete.finished.success,
  ],
  target: jingleCollectionsTableRefreshed,
})
